import type {components} from "./generated";
import type {AuthorizedFetch} from "./client";

export type AiProvider = components["schemas"]["AiProvider"];
export type AiProviderDraft = components["schemas"]["AiProviderDraft"];
export type AiModel = components["schemas"]["AiModel"];
export type AiModelDraft = components["schemas"]["AiModelDraft"];
export type AiWorker = components["schemas"]["AiWorker"];
export type AiWorkerDraft = components["schemas"]["AiWorkerDraft"];
export type AiAdministrationVersion = components["schemas"]["AiAdministrationVersion"];
export type ApprovedTool = components["schemas"]["ApprovedTool"];
export type AiObjectType = "providers" | "models" | "workers";
export type AiLifecycleAction = "validate" | "approve" | "activate" | "suspend" | "retire";

export const listAiProviders = (fetcher: AuthorizedFetch, signal?: AbortSignal) =>
  read<AiProvider[]>(fetcher, "/api/v1/ai-administration/providers", signal);

export const readAiProvider = (fetcher: AuthorizedFetch, id: string, signal?: AbortSignal) =>
  read<AiProvider>(fetcher, `/api/v1/ai-administration/providers/${encodeURIComponent(id)}`, signal);

export const createAiProvider = (fetcher: AuthorizedFetch, draft: AiProviderDraft) =>
  mutate<AiProvider>(fetcher, "/api/v1/ai-administration/providers", "POST", draft);

export const updateAiProvider = (fetcher: AuthorizedFetch, current: AiProvider, draft: AiProviderDraft) =>
  mutate<AiProvider>(fetcher, `/api/v1/ai-administration/providers/${current.id}`, "PUT", {
    expectedVersion: current.version, draft
  });

export const listAiModels = (fetcher: AuthorizedFetch, signal?: AbortSignal) =>
  read<AiModel[]>(fetcher, "/api/v1/ai-administration/models", signal);

export const readAiModel = (fetcher: AuthorizedFetch, id: string, signal?: AbortSignal) =>
  read<AiModel>(fetcher, `/api/v1/ai-administration/models/${encodeURIComponent(id)}`, signal);

export const createAiModel = (fetcher: AuthorizedFetch, draft: AiModelDraft) =>
  mutate<AiModel>(fetcher, "/api/v1/ai-administration/models", "POST", draft);

export const updateAiModel = (fetcher: AuthorizedFetch, current: AiModel, draft: AiModelDraft) =>
  mutate<AiModel>(fetcher, `/api/v1/ai-administration/models/${current.id}`, "PUT", {
    expectedVersion: current.version, draft
  });

export const listAiWorkers = (fetcher: AuthorizedFetch, signal?: AbortSignal) =>
  read<AiWorker[]>(fetcher, "/api/v1/ai-administration/workers", signal);

export const readAiWorker = (fetcher: AuthorizedFetch, id: string, signal?: AbortSignal) =>
  read<AiWorker>(fetcher, `/api/v1/ai-administration/workers/${encodeURIComponent(id)}`, signal);

export const createAiWorker = (fetcher: AuthorizedFetch, draft: AiWorkerDraft) =>
  mutate<AiWorker>(fetcher, "/api/v1/ai-administration/workers", "POST", draft);

export const updateAiWorker = (fetcher: AuthorizedFetch, current: AiWorker, draft: AiWorkerDraft) =>
  mutate<AiWorker>(fetcher, `/api/v1/ai-administration/workers/${current.id}`, "PUT", {
    expectedVersion: current.version, draft
  });

export const listApprovedTools = (fetcher: AuthorizedFetch, signal?: AbortSignal) =>
  read<ApprovedTool[]>(fetcher, "/api/v1/approved-tools", signal);

export function listAiVersions(
  fetcher: AuthorizedFetch,
  type: AiObjectType,
  id: string,
  signal?: AbortSignal
) {
  return read<AiAdministrationVersion[]>(fetcher,
    `/api/v1/ai-administration/${type}/${encodeURIComponent(id)}/versions`, signal);
}

export function transitionAiObject<T>(
  fetcher: AuthorizedFetch,
  type: AiObjectType,
  id: string,
  version: number,
  action: AiLifecycleAction,
  rationale: string
) {
  return mutate<T>(fetcher,
    `/api/v1/ai-administration/${type}/${encodeURIComponent(id)}/${action}`,
    "POST", {expectedVersion: version, rationale});
}

async function read<T>(
  fetcher: AuthorizedFetch,
  path: string,
  signal?: AbortSignal
): Promise<T> {
  return await request<T>(fetcher, path, {
    signal,
    headers: {Accept: "application/json"}
  });
}

async function mutate<T>(
  fetcher: AuthorizedFetch,
  path: string,
  method: "POST" | "PUT",
  body: unknown
): Promise<T> {
  return await request<T>(fetcher, path, {
    method,
    body: JSON.stringify(body),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": crypto.randomUUID(),
      "X-Correlation-ID": crypto.randomUUID()
    }
  });
}

async function request<T>(
  fetcher: AuthorizedFetch,
  path: string,
  init: RequestInit
): Promise<T> {
  const response = await fetcher(path, init);
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as {message?: string} | null;
    throw new Error(problem?.message ?? `AI Administration request failed: ${response.status}`);
  }
  return await response.json() as T;
}
